import {
  Box,
  Card,
  CardContent,
  Checkbox,
  FormControlLabel,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import {
  Control,
  FieldErrors,
  UseFormGetValues,
  UseFormSetValue,
  UseFormTrigger,
  UseFormWatch,
  Controller,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useRef, useEffect } from 'react';
import { startOfDay } from 'date-fns';
import { MaterialAutocompleteField } from '@/shared/components/forms';
import { AppDatePicker } from '@/shared/components';
import { CreateAlertFormValues } from '../types';

interface AlertProductSectionProps {
  readonly control: Control<CreateAlertFormValues>;
  readonly setValue: UseFormSetValue<CreateAlertFormValues>;
  readonly errors: FieldErrors<CreateAlertFormValues>;
  readonly getValues: UseFormGetValues<CreateAlertFormValues>;
  readonly trigger: UseFormTrigger<CreateAlertFormValues>;
  readonly watch: UseFormWatch<CreateAlertFormValues>;
}

/** Returns YYYYMMDD integer from a Date using LOCAL calendar values — timezone-safe for day comparisons. */
const toLocalDay = (d: Date): number =>
  d.getFullYear() * 10000 + (d.getMonth() + 1) * 100 + d.getDate();

/**
 * Product section for the alert creation form.
 * Allows users to select:
 * - Product from the ROCKEASE materials catalog (category + subcategory, required)
 * - Quantity (required, positive number)
 * - Unit (required, from API)
 * - Condition (Neuf / Occasion, optional toggle)
 * - Availability date range (optional, shown when checkbox is checked)
 *
 * Reuses shared MaterialAutocompleteField for consistency with offer search/publish.
 */
export function AlertProductSection({
  control,
  errors,
  setValue,
  getValues,
  trigger,
  watch,
}: AlertProductSectionProps) {
  const { t } = useTranslation();

  const availabilityEnabled = watch('availabilityEnabled');
  const availableFrom = watch('availableFrom');
  const availableTo = watch('availableTo');
  const wasEnabledRef = useRef(false);

  // Auto-populate "From" with today when availability is first enabled; clear dates when disabled.
  useEffect(() => {
    if (availabilityEnabled && !wasEnabledRef.current && availableFrom === null) {
      setValue('availableFrom', startOfDay(new Date()));
    }
    if (!availabilityEnabled && wasEnabledRef.current) {
      setValue('availableFrom', null);
      setValue('availableTo', null);
    }
    wasEnabledRef.current = availabilityEnabled ?? false;
  }, [availabilityEnabled, availableFrom, setValue]);

  // Re-validate date fields whenever either date or the checkbox changes.
  useEffect(() => {
    void trigger(['availableFrom', 'availableTo']);
  }, [availableFrom, availableTo, availabilityEnabled, trigger]);

  return (
    <Card sx={{ mb: 2, borderRadius: 2, boxShadow: 1 }} data-testid="alert-product-section">
      <CardContent>
        {/* Section header */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Box
            sx={{
              bgcolor: '#FFE500',
              borderRadius: 1,
              p: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mr: 1.5,
            }}
          >
            <Inventory2Icon sx={{ color: '#1E293B', fontSize: 20 }} />
          </Box>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {t('alerts.product.title')}
          </Typography>
        </Box>

        {/* Product selection (category + subcategory) */}
        <Box sx={{ mb: 2 }} data-testid="alert-product-field-wrapper">
          <MaterialAutocompleteField<CreateAlertFormValues>
            categoryName="materialCategory"
            subcategoryName="materialSubcategory"
            materialIdName="materialId"
            control={control}
            categoryLabel={t('alerts.product.categoryLabel')}
            subcategoryLabel={t('alerts.product.subcategoryLabel')}
            categoryPlaceholder={t('alerts.product.categoryPlaceholder')}
            subcategoryPlaceholder={t('alerts.product.subcategoryPlaceholder')}
            required
            categoryError={!!errors.materialCategory}
            categoryHelperText={errors.materialCategory?.message}
            subcategoryError={!!errors.materialSubcategory}
            subcategoryHelperText={errors.materialSubcategory?.message}
            requiredMessage={t('alerts.product.required')}
            subcategoryRequiredMessage={t('alerts.product.subcategoryRequired')}
            categoryId="alert-material-category-input"
            subcategoryId="alert-material-subcategory-input"
            categoryAriaLabel={t('alerts.product.categoryLabel')}
            subcategoryAriaLabel={t('alerts.product.subcategoryLabel')}
          />
        </Box>

        {/* Condition toggle — Tous / Neuf / Occasion (optional; '' = All = null on submit) */}
        <Box sx={{ mb: 2 }}>
          <Typography
            sx={{
              display: 'block',
              fontSize: '14px',
              fontWeight: 500,
              mb: 1,
              color: 'text.primary',
            }}
          >
            {t('alerts.product.condition.label')}
          </Typography>
          <Controller
            name="condition"
            control={control}
            render={({ field }) => (
              <ToggleButtonGroup
                {...field}
                exclusive
                onChange={(_event, newValue: string | null) => {
                  if (newValue !== null) field.onChange(newValue);
                }}
                fullWidth
                data-testid="alert-condition-toggle"
                aria-label={t('alerts.product.condition.label')}
                sx={{
                  display: 'flex',
                  '& .MuiToggleButton-root': {
                    py: 0.5,
                    border: '1px solid #E5E7EB',
                    textTransform: 'none',
                    fontSize: '16px',
                    fontWeight: 500,
                    color: 'text.primary',
                    flex: 1,
                    '&.Mui-selected': {
                      backgroundColor: 'primary.main',
                      color: 'white',
                      borderColor: 'primary.main',
                      '&:hover': {
                        backgroundColor: 'primary.dark',
                      },
                    },
                    '&:hover': {
                      backgroundColor: 'rgba(0, 0, 0, 0.04)',
                    },
                  },
                }}
              >
                <ToggleButton
                  value=""
                  data-testid="alert-condition-all"
                  aria-label={t('alerts.product.condition.all')}
                >
                  {t('alerts.product.condition.all')}
                </ToggleButton>
                <ToggleButton
                  value="NEW"
                  data-testid="alert-condition-new"
                  aria-label={t('alerts.product.condition.new')}
                >
                  {t('alerts.product.condition.new')}
                </ToggleButton>
                <ToggleButton
                  value="USED"
                  data-testid="alert-condition-used"
                  aria-label={t('alerts.product.condition.used')}
                >
                  {t('alerts.product.condition.used')}
                </ToggleButton>
              </ToggleButtonGroup>
            )}
          />
        </Box>

        {/* Availability checkbox + date range (always visible, disabled when unchecked) */}
        <Box>
          <Controller
            name="availabilityEnabled"
            control={control}
            render={({ field }) => (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={field.value}
                    onChange={field.onChange}
                    data-testid="alert-availability-checkbox"
                  />
                }
                label={t('alerts.product.availability.label')}
              />
            )}
          />

          <Box
            sx={{
              display: 'flex',
              gap: 2,
              mt: 1,
              flexWrap: { xs: 'wrap', sm: 'nowrap' },
            }}
            data-testid="alert-availability-dates"
          >
            {/* From date */}
            <Box sx={{ flex: 1, minWidth: { xs: '100%', sm: 'auto' } }}>
              <Controller
                name="availableFrom"
                control={control}
                rules={{
                  validate: (value: Date | null) => {
                    if (!availabilityEnabled) return true;
                    if (!value) return t('alerts.product.availability.fromRequired');
                    if (toLocalDay(new Date(value)) < toLocalDay(new Date())) {
                      return t('alerts.product.availability.startPastError');
                    }
                    return true;
                  },
                }}
                render={({ field }) => (
                  <AppDatePicker
                    label={t('alerts.product.availability.from')}
                    required={availabilityEnabled}
                    value={field.value}
                    onChange={value => {
                      field.onChange(value);
                    }}
                    onBlur={() => {
                      field.onBlur();
                      void trigger(['availableFrom', 'availableTo']);
                    }}
                    minDate={new Date()}
                    disabled={!availabilityEnabled}
                    error={!!errors.availableFrom}
                    helperText={errors.availableFrom?.message}
                  />
                )}
              />
            </Box>

            {/* To date */}
            <Box sx={{ flex: 1, minWidth: { xs: '100%', sm: 'auto' } }}>
              <Controller
                name="availableTo"
                control={control}
                rules={{
                  validate: (value: Date | null) => {
                    if (!availabilityEnabled) return true;
                    if (!value) return t('alerts.product.availability.toRequired');
                    const currentStartDate = getValues('availableFrom');
                    if (currentStartDate) {
                      const start = new Date(currentStartDate);
                      const end = new Date(value);
                      if (toLocalDay(end) < toLocalDay(start)) {
                        return t('alerts.product.availability.endBeforeStart');
                      }
                    }
                    return true;
                  },
                }}
                render={({ field }) => (
                  <AppDatePicker
                    label={t('alerts.product.availability.to')}
                    required={availabilityEnabled}
                    value={field.value}
                    onChange={value => {
                      field.onChange(value);
                    }}
                    onBlur={() => {
                      field.onBlur();
                      void trigger(['availableFrom', 'availableTo']);
                    }}
                    minDate={availableFrom ? new Date(availableFrom) : new Date()}
                    disabled={!availabilityEnabled}
                    error={!!errors.availableTo}
                    helperText={errors.availableTo?.message}
                  />
                )}
              />
            </Box>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
}
